import { makeStyles } from '@rentacenter/racstrap';
import React, { useEffect, useState } from 'react';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { getPurchaseOrderById } from '../../api/purchaseOrders';
import { getmenuandstore } from '../../api/store';
import { ApiStateWrapper } from '../../common/ApiStateWrapper/ApiStateWrapper';
import { AppRoute } from '../../config/route-config';
import { PoFormField } from '../../constants/constants';
import { Store } from '../../domain/Store';
import {
  PurchaseOrder,
  PurchaseOrder as PurchaseOrderModel,
  PurchaseOrderFormValues,
} from '../../types/types';
import { toPurchaseOrderFormValues } from '../../utils/purchaseOrderFormMappers';
import { useGlobalContext } from '../../context/global/GlobalContext';
import { PODetailsForm } from './PODetailsForm';

export type PODetailsProps = RouteComponentProps<
  { purchaseOrderId: string; storeNumber: string },
  Record<string, unknown>
>;

export const useStyles = makeStyles({
  contentHeight: {
    position: 'absolute',
    height: '100vh',
    width: '100%',
  },
});

export const PODetails = (props: PODetailsProps) => {
  const classes = useStyles();
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrderModel | null>(
    null
  );
  const [purchaseOrderFormValues, setPurchaseOrderFormValues] =
    useState<PurchaseOrderFormValues | null>(null);
  const [isSaveDraftDisabled, setIsSaveDraftDisabled] = useState(true);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [purchaseOrderNotFound, setPurchaseOrderNotFound] = useState(false);
  const [apiError, setApiError] = useState<Error | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [isStoreLoading, setIsStoreLoading] = useState(false);
  const [searchStoreResult, setSearchStoreResult] = useState<Store[]>([]);
  const {
    match: {
      params: { purchaseOrderId, storeNumber },
    },
    location: { pathname },
  } = props;

  const isCreatePOPath = pathname === AppRoute.Create;
  const { classifications } = useGlobalContext();
  const classificationsLoaded =
    classifications &&
    classifications.isPending === false &&
    !!classifications.classificationsByDepts;

  useEffect(() => {
    const fetchPurchaseOrder = async () => {
      try {
        if (!purchaseOrderId) return;

        const request = {
          purchaseOrderNumber: purchaseOrderId,
          storeNumber: [storeNumber],
        };

        const purchaseOrderResponse = await getPurchaseOrderById(request);

        const purchaseOrders: PurchaseOrder[] =
          purchaseOrderResponse?.purchaseOrders?.map((po: any) => {
            const details = po?.purchaseOrderDetails || [];
            const firstDetail = details[0];
            return {
              id: po?.externalPoNumber,
              purchaseOrderSubType: po?.purchaseOrderSubType,
              referenceNumber: po?.referenceNumber,
              supplier: firstDetail?.supplierName,
              brand: firstDetail?.brand,
              model: firstDetail?.modelNumber,
              itemCondition: firstDetail?.condition,
              department: firstDetail?.department,
              subDepartment: firstDetail?.subDepartment,
              itemType: firstDetail?.itemType,
              itemSubType: firstDetail?.itemSubType,
              bracket: firstDetail?.bracket,
              startClassificationsSelect: PoFormField.Department,
              description: firstDetail?.itemDescription,
              supplierOrderNo: firstDetail?.supplierOrderNumber,
              totalQuantity: firstDetail?.quantityOrdered,
              costPerItem: firstDetail?.itemCost,
              rmsItemNumber: firstDetail?.rmsItemNumber,
              isRcptAvailable: firstDetail?.isRcptAvailable,
              purchaseOrderId: po?.purchaseOrderId,
              totalCost: po?.totalCost,
              createdBy: po?.createdBy,
              allocations: details.map((d: any) => ({
                pricing: d?.pricing,
                quantity: d?.quantityOrdered,
                state: po?.state,
                storeNum: po?.storeNumber,
              })),
              poStatus: po?.poStatusType,
              spendLimitId: po?.spendLimitId,
              purchaseOrderDetails: details,
            };
          });

        purchaseOrders?.length
          ? setPurchaseOrder(purchaseOrders[0])
          : setPurchaseOrderNotFound(true);
      } catch (error: any) {
        setIsSaveDraftDisabled(false);
        setIsSubmitDisabled(false);
        setApiError(error);
      }
    };

    if (purchaseOrderId) {
      fetchPurchaseOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseOrderId]);

  useEffect(() => {
    if (isCreatePOPath) {
      setPurchaseOrder({
        ...purchaseOrder,
        poStatus: '',
        id: '',
      } as PurchaseOrderModel);
      setPurchaseOrderFormValues(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (purchaseOrder) {
      setPurchaseOrderFormValues(toPurchaseOrderFormValues(purchaseOrder));
    }
  }, [purchaseOrder]);

  useEffect(() => {
    async function fetchStores() {
      if (searchTerm.length >= 3 || searchTerm === '') {
        setIsStoreLoading(true);
        const fetchedStores: Store[] = await getmenuandstore('MENU', false);
        setIsStoreLoading(false);
        setSearchStoreResult(fetchedStores as Store[]);
      }
    }
    fetchStores();
  }, [searchTerm]);

  return (
    <>
      <>
        {purchaseOrderNotFound && <Redirect to="/not-found" />}
        {apiError && <Redirect to={AppRoute.Index} />}
        {!purchaseOrderId ||
        (purchaseOrderFormValues && classificationsLoaded) ? (
          <PODetailsForm
            purchaseOrderId={isCreatePOPath ? '' : purchaseOrderId}
            purchaseOrder={purchaseOrderFormValues}
            stores={searchStoreResult}
            isSaveDraftDisabled={isSaveDraftDisabled}
            setIsSaveDraftDisabled={setIsSaveDraftDisabled}
            isSubmitDisabled={isSubmitDisabled}
            setIsSubmitDisabled={setIsSubmitDisabled}
            onSearch={setSearchTerm}
            isSearchLoading={isStoreLoading}
            {...props}
          />
        ) : (
          <ApiStateWrapper
            loading
            hasApiError={purchaseOrderNotFound}
            successContent={<></>}
            classes={{
              loader: classes.contentHeight,
              apiError: classes.contentHeight,
            }}
          />
        )}
      </>
    </>
  );
};
